import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Users2, Megaphone, Save, ArrowRight, TrendingUp, FileDown } from "lucide-react";
import { INFLUENCE_META } from "@/components/RegistryPanel";

const ENGAGEMENT = [
  { key: "unaware", label: "Unaware" },
  { key: "resistant", label: "Resistant" },
  { key: "neutral", label: "Neutral" },
  { key: "supportive", label: "Supportive" },
  { key: "leading", label: "Leading" },
];
const FREQUENCIES = ["Daily", "Weekly", "Bi-weekly", "Monthly", "Quarterly", "Ad hoc"];

export default function StakeholderView({ projectId }) {
  const [data, setData] = useState(null);

  const load = () => api.get(`/projects/${projectId}/stakeholder-analysis`).then(({ data }) => setData(data)).catch(() => setData({ stakeholders: [], comm_plan: [] }));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!data.stakeholders.length) {
    return (
      <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="stakeholders-empty">
        <Users2 className="h-8 w-8 text-primary mx-auto mb-3" />
        <p className="text-muted-foreground">Add resources to this project (Resources tab) to auto-populate the stakeholder analysis and communication plan. Set each person's level of influence in the Resource registry.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="stakeholder-view">
      <div className="flex justify-end -mb-4">
        <ExportPackButton projectId={projectId} />
      </div>
      <EngagementMatrix projectId={projectId} stakeholders={data.stakeholders} onSaved={load} />
      <CommPlan projectId={projectId} rows={data.comm_plan} onSaved={load} />
      <EscalationPlan projectId={projectId} />
    </div>
  );
}

function ExportPackButton({ projectId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/stakeholder-pack-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "stakeholder-pack.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Stakeholder pack downloaded");
    } catch (e) {
      toast.error("Could not generate the stakeholder pack PDF");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="gap-1.5" onClick={download} disabled={busy} data-testid="export-stakeholder-pack">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Export stakeholder pack (PDF)
    </Button>
  );
}

function EscalationRow({ projectId, row }) {
  const [trigger, setTrigger] = useState(row.trigger);
  const [respond, setRespond] = useState(row.respond_within);
  const [saving, setSaving] = useState(false);
  const dirty = trigger !== row.trigger || respond !== row.respond_within;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/escalation-plan`, {
        level: row.level, label: row.label, trigger, respond_within: respond, notes: row.notes || "",
      });
      toast.success(`Level ${row.level} escalation updated`);
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-lg p-4 flex gap-4" data-testid={`escalation-level-${row.level}`}>
      <div className="shrink-0 h-9 w-9 rounded-full bg-primary text-primary-foreground font-display font-extrabold flex items-center justify-center">L{row.level}</div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold">{row.label}</p>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-secondary text-muted-foreground">respond within</span>
          <Input value={respond} onChange={(e) => setRespond(e.target.value)} className="h-7 w-40 text-xs" data-testid={`escalation-respond-${row.level}`} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {row.contacts.length ? row.contacts.map((c) => (
            <span key={c.name} className="inline-flex items-center gap-1.5 border border-border rounded-full px-2.5 py-1 text-[11px] bg-secondary/30" title={[c.email, c.phone].filter(Boolean).join(" · ")} data-testid={`escalation-contact-${c.name}`}>
              <b>{c.name}</b>
              {c.role && <span className="text-muted-foreground">{c.role}</span>}
              {c.email && <span className="text-muted-foreground hidden sm:inline">{c.email}</span>}
            </span>
          )) : (
            <span className="text-xs text-muted-foreground">
              {row.level === 4 ? "No decision makers found — set influence to \"Decision maker\" in the Resource registry." : row.level === 3 ? "No line managers found — set \"Reports to\" links in the Resource registry." : "No contacts yet."}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input value={trigger} onChange={(e) => setTrigger(e.target.value)} className="h-8 text-xs" data-testid={`escalation-trigger-${row.level}`} />
          <Button size="sm" variant={dirty ? "default" : "outline"} className="h-8 px-2 text-xs gap-1 shrink-0" onClick={save} disabled={saving || !dirty} data-testid={`escalation-save-${row.level}`}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function EscalationPlan({ projectId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/escalation-plan`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [projectId]);
  if (!rows) return null;
  return (
    <div className="border border-border rounded-lg p-5" data-testid="escalation-plan">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="h-4 w-4 text-primary" />
        <p className="font-display font-bold">Escalation plan</p>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Auto-built from the org chart (reports-to links) and influence levels — L1 team, L2 you, L3 line managers, L4 decision makers. Edit any trigger or response time and save.</p>
      <div className="space-y-3">
        {rows.map((r) => <EscalationRow key={r.level} projectId={projectId} row={r} />)}
      </div>
    </div>
  );
}

function EngagementMatrix({ projectId, stakeholders, onSaved }) {
  const [savingFor, setSavingFor] = useState(null);

  const saveRow = async (s, patch) => {
    setSavingFor(s.name);
    try {
      await api.put(`/projects/${projectId}/stakeholders`, {
        name: s.name,
        engagement_current: patch.engagement_current ?? s.engagement_current,
        engagement_desired: patch.engagement_desired ?? s.engagement_desired,
        notes: s.notes || "",
      });
      onSaved();
    } catch (e) { toast.error("Could not save engagement"); }
    finally { setSavingFor(null); }
  };

  return (
    <div className="border border-border rounded-lg p-5" data-testid="engagement-matrix">
      <div className="flex items-center gap-2 mb-1">
        <Users2 className="h-4 w-4 text-primary" />
        <p className="font-display font-bold">Stakeholder engagement assessment</p>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Industry-standard engagement matrix — <b>C</b> = current, <b>D</b> = desired. A gap between C and D signals where engagement effort is needed.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground bg-secondary/40">
              <th className="px-3 py-2 font-semibold">Stakeholder</th>
              <th className="px-3 py-2 font-semibold">Influence</th>
              {ENGAGEMENT.map((e) => <th key={e.key} className="px-3 py-2 font-semibold text-center">{e.label}</th>)}
              <th className="px-3 py-2 font-semibold">Set C / D</th>
            </tr>
          </thead>
          <tbody>
            {stakeholders.map((s) => {
              const gap = s.engagement_current !== s.engagement_desired;
              return (
                <tr key={s.name} className="border-t border-border" data-testid={`stakeholder-row-${s.name}`}>
                  <td className="px-3 py-2">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-[11px] text-muted-foreground">{[s.role, s.organization].filter(Boolean).join(" · ")}</p>
                  </td>
                  <td className="px-3 py-2">
                    {s.influence && INFLUENCE_META[s.influence]
                      ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${INFLUENCE_META[s.influence].cls}`}>{INFLUENCE_META[s.influence].label}</span>
                      : <span className="text-[11px] text-muted-foreground/60">set in registry</span>}
                  </td>
                  {ENGAGEMENT.map((e) => (
                    <td key={e.key} className="px-3 py-2 text-center">
                      {s.engagement_current === e.key && <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold" title="Current">C</span>}
                      {s.engagement_desired === e.key && <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-emerald-500 text-emerald-600 text-[10px] font-bold ${s.engagement_current === e.key ? "ml-1" : ""}`} title="Desired">D</span>}
                      {gap && s.engagement_current !== e.key && s.engagement_desired !== e.key &&
                        ENGAGEMENT.findIndex((x) => x.key === e.key) > ENGAGEMENT.findIndex((x) => x.key === s.engagement_current) &&
                        ENGAGEMENT.findIndex((x) => x.key === e.key) < ENGAGEMENT.findIndex((x) => x.key === s.engagement_desired) &&
                        <ArrowRight className="h-3 w-3 text-muted-foreground/40 inline" />}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Select value={s.engagement_current} onValueChange={(v) => saveRow(s, { engagement_current: v })}>
                        <SelectTrigger className="h-7 w-28 text-xs" data-testid={`engagement-current-${s.name}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ENGAGEMENT.map((e) => <SelectItem key={e.key} value={e.key}>C: {e.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={s.engagement_desired} onValueChange={(v) => saveRow(s, { engagement_desired: v })}>
                        <SelectTrigger className="h-7 w-28 text-xs" data-testid={`engagement-desired-${s.name}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ENGAGEMENT.map((e) => <SelectItem key={e.key} value={e.key}>D: {e.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {savingFor === s.name && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CommPlanRow({ projectId, row, onSaved }) {
  const [method, setMethod] = useState(row.method);
  const [frequency, setFrequency] = useState(row.frequency);
  const [goal, setGoal] = useState(row.goal);
  const [owner, setOwner] = useState(row.owner);
  const [saving, setSaving] = useState(false);
  const dirty = method !== row.method || frequency !== row.frequency || goal !== row.goal || owner !== row.owner;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/comm-plan`, { name: row.name, method, frequency, goal, owner, notes: row.notes || "" });
      toast.success(`Communication plan updated for ${row.name}`);
      onSaved();
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <tr className="border-t border-border" data-testid={`commplan-row-${row.name}`}>
      <td className="px-3 py-2">
        <p className="font-medium whitespace-nowrap">{row.name}</p>
        {row.influence && INFLUENCE_META[row.influence] &&
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${INFLUENCE_META[row.influence].cls}`}>{INFLUENCE_META[row.influence].label}</span>}
      </td>
      <td className="px-3 py-2"><Input value={method} onChange={(e) => setMethod(e.target.value)} className="h-8 text-xs min-w-[200px]" data-testid={`commplan-method-${row.name}`} /></td>
      <td className="px-3 py-2">
        <Select value={frequency} onValueChange={setFrequency}>
          <SelectTrigger className="h-8 w-28 text-xs" data-testid={`commplan-frequency-${row.name}`}><SelectValue /></SelectTrigger>
          <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2"><Input value={goal} onChange={(e) => setGoal(e.target.value)} className="h-8 text-xs min-w-[150px]" /></td>
      <td className="px-3 py-2"><Input value={owner} onChange={(e) => setOwner(e.target.value)} className="h-8 text-xs w-28" /></td>
      <td className="px-3 py-2">
        <Button size="sm" variant={dirty ? "default" : "outline"} className="h-7 px-2 text-xs gap-1" onClick={save} disabled={saving || !dirty} data-testid={`commplan-save-${row.name}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
        </Button>
      </td>
    </tr>
  );
}

function CommPlan({ projectId, rows, onSaved }) {
  return (
    <div className="border border-border rounded-lg p-5" data-testid="comm-plan">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="h-4 w-4 text-primary" />
        <p className="font-display font-bold">Communication plan</p>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Starter plan generated from each stakeholder's influence level — edit any row and save. Decision makers get the tightest cadence; observers the lightest.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground bg-secondary/40">
              {["Stakeholder", "Method / channel", "Frequency", "Goal", "Owner", ""].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <CommPlanRow key={r.name} projectId={projectId} row={r} onSaved={onSaved} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
